import React, { useState } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils2";

/**
 * Click-to-edit due-date button. Opens a Calendar popover, saves to backend.
 * Props:
 *   scheduleId: schedule_id
 *   currentDate: ISO string
 *   onUpdated: callback after save (refreshes parent)
 *   className: extra styles on the trigger button
 *   children: optional custom trigger label (defaults to formatted date)
 */
export default function EditDateButton({ scheduleId, currentDate, onUpdated, className = "", children }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const date = currentDate ? new Date(currentDate) : new Date();

  const onSelect = async (newDate) => {
    if (!newDate) return;
    setSaving(true);
    try {
      // Preserve original time-of-day to avoid timezone drift
      const orig = new Date(currentDate);
      newDate.setHours(orig.getHours() || 9, orig.getMinutes() || 0, 0, 0);
      await api.put(`/schedule/${scheduleId}`, { due_date: newDate.toISOString() });
      toast.success("Date updated");
      setOpen(false);
      onUpdated?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`inline-flex items-center gap-1 hover:text-orange-500 transition-colors cursor-pointer ${className}`}
          data-testid={`edit-date-${scheduleId}`}
          disabled={saving}
        >
          <CalendarIcon className="w-3 h-3" />
          {children}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-zinc-900 border-zinc-800 rounded-sm" align="end">
        <Calendar
          mode="single"
          selected={date}
          onSelect={onSelect}
          initialFocus
          className="bg-zinc-900 text-zinc-100"
        />
      </PopoverContent>
    </Popover>
  );
}
